// ABOUTME: The 5D compass drawn from the summary model, with the v28 asset's geometry and this crossing's wedges lit.
// ABOUTME: Each ring is turned so its first lit wedge sits under the north arrow; the guiding truth runs along the rim.

import { useId } from 'react';
import {
  COMPASS_FAINT_OUTER_STROKE,
  COMPASS_NORTH_ARROW,
  COMPASS_RIM,
  COMPASS_RIM_UNDERLAY,
  COMPASS_WEDGE_FAINT_FILL,
  COMPASS_WEDGE_FAINT_INK,
  COMPASS_WEDGE_FAINT_STROKE,
  COMPASS_WEDGE_FILLS,
  COMPASS_WEDGE_LIT_INK,
  COMPASS_WEDGE_LIT_STROKE,
  CROSSING_GOLD_RIM_TEXT,
} from '../../../../theme/crossingTokens';
import type { Summary, SummaryRing, SummaryRingKey } from './summaryModel';

const CENTRE = 300;
const LABEL_OFFSET = 3.3; // the label arc sits this far inside the ring's middle on the upper half, outside it on the lower
const FAINT_RINGS_OPACITY = 0.68;
const STAR_SCALE_AT_EXIT = 3;
const FONT_FAMILY = "'Lato', 'Helvetica Neue', Arial, sans-serif";

interface RingGeometry {
  innerR: number;
  outerR: number;
  fontSize: number;
}

// Innermost ring first, as the asset draws them.
const RING_GEOMETRY: Record<SummaryRingKey, RingGeometry> = {
  feeling: { innerR: 120, outerR: 158, fontSize: 9.5 },
  storm: { innerR: 158, outerR: 194, fontSize: 9 },
  weather: { innerR: 194, outerR: 230, fontSize: 8.5 },
  reached: { innerR: 230, outerR: 262, fontSize: 9.5 },
  nextMove: { innerR: 262, outerR: 288, fontSize: 8.5 },
};

const TRUTH_RADIUS = 293.5;
const RIM_RADIUS = 300;
const TRUTH_ADVANCE = 7.3; // one upper-cased character of the 8.5px bold rim text plus its 1.4 letter-spacing
const STAR_LEAD = 18; // the star sits this far before the first letter
const RIM_END_PADDING = 14; // band left past the star and the last letter
const MIN_BAND_HALF_ANGLE = 22.5; // a short truth keeps the asset's 45deg band
const MIN_TRUTH_HALF_ANGLE = 50; // and the asset's 100deg text path
const MAX_HALF_ANGLE = 175;

const rimTruthText = (truth: string) => truth.toUpperCase();

interface RimLayout {
  bandPath: string;
  truthPath: string;
  textLength: number;
  starOffset: string;
  textOffset: string;
}

const toDegrees = (length: number, radius: number) => (length / radius) * (180 / Math.PI);

// The band and the text path grow with the truth, centred on north, so the whole truth always sits on the band.
function rimLayoutFor(truth: string): RimLayout {
  const textLength = rimTruthText(truth).length * TRUTH_ADVANCE;
  const span = STAR_LEAD + textLength;
  const bandHalf = Math.min(MAX_HALF_ANGLE, Math.max(MIN_BAND_HALF_ANGLE, toDegrees(span / 2 + RIM_END_PADDING, TRUTH_RADIUS)));
  const truthHalf = Math.max(MIN_TRUTH_HALF_ANGLE, bandHalf);
  const arc = (radius: number, half: number) =>
    `M${coords(radius, -half)} A${radius} ${radius} 0 ${half > 90 ? 1 : 0} 1 ${coords(radius, half)}`;
  const pathLength = 2 * truthHalf * (Math.PI / 180) * TRUTH_RADIUS;
  const starAt = (pathLength - span) / 2;
  return {
    bandPath: arc(RIM_RADIUS, bandHalf),
    truthPath: arc(TRUTH_RADIUS, truthHalf),
    textLength,
    starOffset: `${((starAt / pathLength) * 100).toFixed(2)}%`,
    textOffset: `${(((starAt + STAR_LEAD) / pathLength) * 100).toFixed(2)}%`,
  };
}

const fmt = (value: number) => value.toFixed(1);

// Degrees clockwise from north to a point on a circle round the compass centre.
function coords(radius: number, degrees: number): string {
  const radians = (degrees * Math.PI) / 180;
  return `${fmt(CENTRE + radius * Math.sin(radians))} ${fmt(CENTRE - radius * Math.cos(radians))}`;
}

function wedgePath(innerR: number, outerR: number, from: number, to: number): string {
  return `M${coords(innerR, from)} A${innerR} ${innerR} 0 0 1 ${coords(innerR, to)} L${coords(outerR, to)} A${outerR} ${outerR} 0 0 0 ${coords(outerR, from)} Z`;
}

// Text on the lower half runs right to left so it reads upright; the rest runs clockwise.
function labelPath(radius: number, from: number, to: number, reversed: boolean): string {
  return reversed
    ? `M${coords(radius, to)} A${radius} ${radius} 0 0 0 ${coords(radius, from)}`
    : `M${coords(radius, from)} A${radius} ${radius} 0 0 1 ${coords(radius, to)}`;
}

// Whether a wedge centred at this angle lands on the lower half once its ring has turned by the ring's rotation.
function landsOnLowerHalf(centreDegrees: number, rotation: number): boolean {
  const turned = (((centreDegrees + rotation) % 360) + 360) % 360;
  return turned > 90 + 1e-6 && turned < 270 - 1e-6;
}

interface WedgeShape {
  code: string;
  label: string;
  lit: boolean;
  wedge: string;
  labelArc: string;
  arcId: string;
}

function shapesOf(ring: SummaryRing, idPrefix: string): WedgeShape[] {
  const { innerR, outerR } = RING_GEOMETRY[ring.key];
  const middle = (innerR + outerR) / 2;
  const step = 360 / ring.wedges.length;
  return ring.wedges.map((wedge, index) => {
    const centre = index * step;
    const from = centre - step / 2;
    const to = centre + step / 2;
    const lower = landsOnLowerHalf(centre, ring.rotation);
    return {
      ...wedge,
      wedge: wedgePath(innerR, outerR, from, to),
      labelArc: labelPath(lower ? middle + LABEL_OFFSET : middle - LABEL_OFFSET, from, to, lower),
      arcId: `${idPrefix}-${ring.key}-${index}`,
    };
  });
}

interface WedgeProps {
  shape: WedgeShape;
  ring: SummaryRingKey;
}

function Wedge({ shape, ring }: WedgeProps) {
  const { fontSize } = RING_GEOMETRY[ring];
  return (
    <g data-ring-wedge={`${ring}-${shape.code}`} data-lit={shape.lit}>
      {shape.lit ? (
        <path d={shape.wedge} fill={COMPASS_WEDGE_FILLS[ring]} stroke={COMPASS_WEDGE_LIT_STROKE} strokeWidth="1.6" strokeLinejoin="round" />
      ) : (
        <path d={shape.wedge} fill={COMPASS_WEDGE_FAINT_FILL} stroke={COMPASS_WEDGE_FAINT_STROKE} strokeWidth="1" />
      )}
      <text fontSize={fontSize} fontWeight="800" letterSpacing="1.1" fill={shape.lit ? COMPASS_WEDGE_LIT_INK : COMPASS_WEDGE_FAINT_INK}>
        <textPath href={`#${shape.arcId}`} startOffset="50%" textAnchor="middle">{shape.label.toUpperCase()}</textPath>
      </text>
    </g>
  );
}

interface SummaryCompassProps {
  summary: Summary;
  starScaled: boolean;
  ringsFaded: boolean;
}

export function SummaryCompass({ summary, starScaled, ringsFaded }: SummaryCompassProps) {
  const idPrefix = useId().replace(/:/g, '');
  const truthId = `${idPrefix}-truth`;
  const rim = rimLayoutFor(summary.truth);
  const rings = summary.rings.map(ring => ({ ring, shapes: shapesOf(ring, idPrefix) }));
  const turn = (ring: SummaryRing) => `rotate(${ring.rotation} ${CENTRE} ${CENTRE})`;

  return (
    <svg viewBox="-20 -24 640 644" width="640" height="644" overflow="visible" fontFamily={FONT_FAMILY} aria-hidden="true" data-testid="summary-compass">
      <defs>
        {rings.flatMap(({ shapes }) => shapes.map(shape => <path key={shape.arcId} id={shape.arcId} d={shape.labelArc} />))}
        <path id={truthId} d={rim.truthPath} />
      </defs>
      <g data-testid="summary-compass-faint" style={{ opacity: ringsFaded ? 0 : FAINT_RINGS_OPACITY, transition: 'opacity 1s ease' }}>
        <circle cx={CENTRE} cy={CENTRE} r="120" fill="none" stroke={COMPASS_WEDGE_FAINT_STROKE} strokeWidth="1" />
        <circle cx={CENTRE} cy={CENTRE} r="288" fill="none" stroke={COMPASS_FAINT_OUTER_STROKE} strokeWidth="1" />
        <circle cx={CENTRE} cy={CENTRE} r="300" fill="none" stroke={COMPASS_RIM_UNDERLAY} strokeWidth="12" />
        {rings.map(({ ring, shapes }) => (
          <g key={ring.key} transform={turn(ring)}>
            {shapes.filter(shape => !shape.lit).map(shape => <Wedge key={shape.code} shape={shape} ring={ring.key} />)}
          </g>
        ))}
      </g>
      <path data-testid="summary-compass-band" d={rim.bandPath} fill="none" stroke={COMPASS_RIM} strokeWidth="12" />
      {rings.map(({ ring, shapes }) => (
        <g key={ring.key} data-ring={ring.key} transform={turn(ring)}>
          {shapes.filter(shape => shape.lit).map(shape => <Wedge key={shape.code} shape={shape} ring={ring.key} />)}
        </g>
      ))}
      <text fontSize="8.5" fontWeight="700" letterSpacing="1.4" fill={CROSSING_GOLD_RIM_TEXT}>
        <textPath href={`#${truthId}`} startOffset={rim.textOffset} textAnchor="start" textLength={rim.textLength} lengthAdjust="spacing">{rimTruthText(summary.truth)}</textPath>
      </text>
      <text
        data-testid="summary-compass-star"
        fontSize="10"
        fontWeight="700"
        fill={CROSSING_GOLD_RIM_TEXT}
        style={{ transformBox: 'fill-box', transformOrigin: '50% 50%', transform: `scale(${starScaled ? STAR_SCALE_AT_EXIT : 1})`, transition: 'transform .9s cubic-bezier(.3,.7,.3,1)' }}
      >
        <textPath href={`#${truthId}`} startOffset={rim.starOffset} textAnchor="middle">★</textPath>
      </text>
      <path d="M300 -14 L308 0 L292 0 Z" fill={COMPASS_NORTH_ARROW} />
    </svg>
  );
}
